import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

export const DataExportPanel = ({ isPlatformAdmin = false, defaultCompanyId = '' }) => {
  const [collections, setCollections] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedScope, setSelectedScope] = useState(isPlatformAdmin ? 'all' : (defaultCompanyId || ''));
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const scopeParams = useMemo(() => {
    if (isPlatformAdmin && selectedScope !== 'all' && selectedScope) {
      return { company_id: selectedScope };
    }
    if (!isPlatformAdmin && defaultCompanyId) {
      return { company_id: defaultCompanyId };
    }
    return {};
  }, [isPlatformAdmin, selectedScope, defaultCompanyId]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const fetchCollections = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [collectionRes, companiesRes] = await Promise.all([
        axios.get(`${API}/data-exports/collections`, { params: scopeParams }),
        isPlatformAdmin ? axios.get(`${API}/platform/companies`) : Promise.resolve({ data: [] }),
      ]);

      setCollections(collectionRes.data?.collections || []);
      setCompanies(companiesRes.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load export options');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, scopeParams]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const handleDownloadCollection = async (key) => {
    try {
      setBusyKey(key);
      const res = await axios.get(`${API}/data-exports/collection/${key}`, {
        params: scopeParams,
        responseType: 'blob',
      });
      const filename = `reify-export-${key}.json`;
      downloadBlob(res.data, filename);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download collection export');
    } finally {
      setBusyKey('');
    }
  };

  const handleDownloadBundle = async () => {
    try {
      setBusyKey('bundle');
      const res = await axios.get(`${API}/data-exports/bundle`, {
        params: scopeParams,
        responseType: 'blob',
      });
      downloadBlob(res.data, 'reify-export-bundle.zip');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download full export bundle');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4" data-testid="data-export-panel">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-100" data-testid="data-export-title">Data Exports</h3>
          <p className="text-xs text-gray-500" data-testid="data-export-subtitle">
            Exports run against the currently connected LIVE deployment database.
          </p>
        </div>
        <button
          onClick={handleDownloadBundle}
          disabled={busyKey === 'bundle' || loading}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          data-testid="download-full-export-bundle-btn"
        >
          {busyKey === 'bundle' ? 'Preparing Bundle…' : 'Download Full Bundle (.zip)'}
        </button>
      </div>

      {isPlatformAdmin && (
        <div className="flex items-center gap-3" data-testid="data-export-scope-row">
          <label className="text-xs text-gray-400 uppercase tracking-wider">Scope</label>
          <select
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200"
            value={selectedScope}
            onChange={(e) => setSelectedScope(e.target.value)}
            data-testid="data-export-scope-select"
          >
            <option value="all">All Companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={fetchCollections}
            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded text-xs"
            data-testid="data-export-refresh-scope-btn"
          >
            Refresh
          </button>
        </div>
      )}

      {error && (
        <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="data-export-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500 text-sm" data-testid="data-export-loading">Loading export options…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="data-export-collection-grid">
          {collections.map((collection) => (
            <div
              key={collection.key}
              className="border border-gray-800 rounded-lg px-3 py-3 flex items-center justify-between gap-3"
              data-testid={`data-export-collection-card-${collection.key}`}
            >
              <div>
                <p className="text-sm text-gray-100 font-medium" data-testid={`data-export-collection-label-${collection.key}`}>{collection.label}</p>
                <p className="text-xs text-gray-500" data-testid={`data-export-collection-key-${collection.key}`}>{collection.key}.json</p>
              </div>
              <button
                onClick={() => handleDownloadCollection(collection.key)}
                disabled={busyKey === collection.key}
                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 rounded text-xs font-medium disabled:opacity-50"
                data-testid={`download-export-${collection.key}-btn`}
              >
                {busyKey === collection.key ? 'Preparing…' : 'Download JSON'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
